import { MessagingSdk } from 'lib-klab-sdks';

/**
 * UI-shaped domain models for the chat dashboard.
 *
 * <p>These match the legacy shapes used by the existing UI components
 * (see lib-klab-micros-dashboard-ui/.../klab-chat.types.ts). They are
 * intentionally kept here as the canonical UI contract so the
 * ChatStateService can map AsyncAPI payloads → UI shapes without the
 * core lib depending on the UI lib.
 *
 * <p>If the UI is refactored later to consume AsyncAPI payloads directly,
 * these can be deleted.
 */

export interface ChatContactUI {
  id: string;
  userId?: number | null;
  employeeId?: number | null;
  name: string;
  searchText?: string;
  role: string;
  participantRole?: 'MEMBER' | 'MODERATOR' | 'ADMIN';
  isAssignedAdmin?: boolean;
  avatarUrl?: string;
  color?: string;
  isConnected: boolean;
  unreadCount?: number;
  lastSeen?: string;
  lastActivityAt?: number;
  lastMessageAt?: number;
  /** Preview del último mensaje de una conversación DIRECT (texto o fallback por tipo), estilo WhatsApp. Solo poblado para contactos con conversación real (ver mapDirectSnapshotAsContact). */
  lastMessagePreview?: string;
  /** true si la contraparte de este DIRECT fue confirmada como eliminada en People (MOMSG-910). No afecta la visibilidad del historial (decisión 6.1). */
  isDeleted?: boolean;
  /** true si el usuario actual fijó esta conversación (participants.$.pinnedAt != null). Preferencia privada por participante, nunca refleja el pin de otro. */
  isPinned?: boolean;
}

/**
 * Entrada de "Vistos por" (MOMSG-1081, extensión): contacto + timestamp
 * ISO 8601 en que vio el mensaje, o null cuando no hay dato histórico
 * (participante legacy — el consumidor debe caer a presencia, no a una
 * fecha de 1970). Tipo puntual devuelto por
 * {@link ChatStateService.getMessageSeenBy} — no se agrega `readAt` a
 * {@link ChatContactUI} en general porque ese tipo se usa en muchos otros
 * contextos sin este campo.
 */
export interface ChatSeenByEntryUI {
  contact: ChatContactUI;
  readAt: string | null;
}

export interface ChatGroupUI {
  id: string;
  name: string;
  shortName: string;
  bgColor: string;
  textColor: string;
  iconUrl?: string;
  unreadCount?: number;
  memberCount?: number;
  lastActivityAt?: number;
  canManage?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  /** true si el participante actual es ADMIN o MODERATOR del grupo: puede eliminar mensajes de cualquier otro participante (MOMSG-1027, extendido a MODERATOR en MOMSG-1038). Distinto de canDelete, que significa "borrar el grupo entero". */
  canDeleteMessages?: boolean;
  canDesignate?: boolean;
  /** true si el usuario actual fijó esta conversación (participants.$.pinnedAt != null). Preferencia privada por participante, nunca refleja el pin de otro. */
  isPinned?: boolean;
  /** Flag crudo del grupo (snap.groupInfo.onlyAdminsCanSend), sin resolver por rol. Necesario para pre-marcar el switch del modal de editar (MOMSG-1053). */
  onlyAdminsCanSend?: boolean;
  /** true si el participante actual puede enviar mensajes en este grupo: siempre true salvo que onlyAdminsCanSend esté activo y el rol actual sea MEMBER (MOMSG-1053). */
  canSendMessages?: boolean;
}

/**
 * Quote/preview de un mensaje al que se está respondiendo. Se renderiza
 * arriba de la burbuja del mensaje hijo.
 */
export interface ChatMessageReplyUI {
  /** id del mensaje original al que se responde. Puede no estar cargado en el cliente. */
  messageId: string;
  /** senderId del autor del mensaje original (stringified userId). */
  senderId: string;
  /** Nombre resoluble del autor original; si no está disponible la UI debe mostrar un fallback no numérico. */
  senderName?: string;
  /** Preview del contenido original (truncado por el server). */
  contentPreview: string;
  isUnavailable?: boolean;
}

export interface ChatMessageForwardedUI {
  isForwarded: boolean;
  sourceMessageId: string;
  sourceConversationId: string;
  forwardedBy: number;
}

export interface ChatMessageUI {
  id: string;
  sequence?: number;
  clientMessageId?: string | null;
  senderId: string;
  senderName?: string;
  senderColor?: string;
  /** Sender identity snapshot carried by live/history records for cached actor resolution. */
  senderSnapshot?: MessagingSdk.SenderSnapshot;
  content: string;
  mentions?: MessagingSdk.MessageMention[] | null;
  type?: 'TEXT' | 'FILE' | 'IMAGE' | 'SYSTEM';
  s3Key?: string | null;
  url?: string | null;
  mimeType?: string | null;
  mediaKind?: ChatUploadMediaKind | null;
  durationMs?: number | null;
  fileName?: string | null;
  fileSize?: number | null;
  imageUrl?: string | null;
  downloadUrl?: string | null;
  timestamp: string;
  status: 'sent' | 'delivered' | 'read';
  isOwn: boolean;
  isPinned?: boolean;
  /** Quote del mensaje original cuando este mensaje es una respuesta. */
  replyTo?: ChatMessageReplyUI | null;
  /** Metadata for forwarded messages. UI renders only a privacy-safe label. */
  forwarded?: ChatMessageForwardedUI | null;
  /** True cuando el mensaje fue editado. La UI agrega "(editado)" junto al timestamp. */
  isEdited?: boolean;
  /** True cuando el mensaje fue eliminado para todos. Reemplaza el contenido por placeholder. */
  isDeleted?: boolean;
  /** UUID de álbum de imágenes (MOMSG-1172), compartido por todas las fotos del lote. null/undefined si el mensaje no pertenece a un álbum. */
  albumId?: string | null;
  /** Posición 0-based dentro del álbum, según el orden de selección del usuario. */
  albumIndex?: number | null;
  /** Cantidad total de fotos que el cliente declaró para este álbum. */
  albumCount?: number | null;
  /** Raw per-identity reaction rows. This is the only reaction source used by UI selectors. */
  reactions: MessagingSdk.MessageReaction[];
  /** Monotonic Store revision for the complete reaction snapshot. */
  reactionsRevision: number;
  /** Raw canonical server timestamp, kept for internal optimistic reconciliation only. */
  serverSentAt?: string;
  /** Client creation time for optimistic local messages, kept for internal reconciliation only. */
  optimisticCreatedAtMs?: number;
  /** Local object URL for optimistic media preview; revoked when canonical echo replaces it. */
  localObjectUrl?: string;
  /** Correlates with ChatUploadManager while an optimistic FILE/IMAGE message's attachment is uploading — used for progress/cancel/retry wiring. Absent on persisted messages. */
  clientFileId?: string | null;
  /** Upload lifecycle of this optimistic message's attachment. undefined/null means the message is already persisted (normal rendering). */
  uploadState?: 'uploading' | 'finalizing' | 'failed' | 'cancelled' | 'interrupted' | null;
  /** 0-100, only meaningful while uploadState === 'uploading'. */
  uploadProgress?: number | null;
}

export const CHAT_ATTACHMENT_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/** Media kinds accepted by the chat upload API (M2/D contract). */
export type ChatUploadMediaKind = "GENERAL" | "ATTACHED_AUDIO" | "VOICE_NOTE";

/** Upload mode decided by the server during INIT. */
export type ChatUploadMode = "SINGLE" | "MULTIPART";

/**
 * Sender-local bubble state for a file being uploaded. The bubble lives only
 * in the sender's live-app UI (manager-owned jobs); peers never see it until
 * the message is persisted and broadcast after COMPLETE.
 */
export type ChatUploadBubbleState =
  | "pending"
  | "uploading"
  | "completed"
  | "failed"
  | "cancelled";

export interface ChatUploadBubbleUI {
  conversationId: string;
  clientFileId: string;
  fileName: string;
  declaredMime: string;
  size: number;
  mediaKind: ChatUploadMediaKind;
  durationMs?: number | null;
  state: ChatUploadBubbleState;
  progress: number;
  uploadedBytes: number;
  error?: string | null;
  reference?: string | null;
  receipt?: string | null;
  /** Local object URL created at enqueue time for image preview during upload. Revoked by the manager when the job settles. */
  localObjectUrl?: string | null;
}

export const CHAT_UPLOAD_GENERAL_MAX_BYTES = 2 * 1024 * 1024 * 1024;
export const CHAT_UPLOAD_SINGLE_MAX_BYTES = 16 * 1024 * 1024;
export const CHAT_UPLOAD_PART_BYTES = 16 * 1024 * 1024;
export const CHAT_UPLOAD_BATCH_LIMIT = 30;
export const CHAT_UPLOAD_GLOBAL_CONCURRENCY = 4;

export const CHAT_UPLOAD_ALLOWED_GENERAL_MIMES = new Set([
  "image/avif", "image/gif", "image/heic", "image/jpeg", "image/png", "image/webp",
  "application/pdf", "application/msword", "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "audio/mpeg", "audio/wav", "audio/ogg", "audio/flac", "audio/mp4", "audio/webm",
  "video/mp4", "application/zip", "application/gzip", "application/x-7z-compressed",
  "text/plain",
]);

export const CHAT_UPLOAD_ALLOWED_AUDIO_MIMES = new Set([
  "audio/mpeg", "audio/wav", "audio/ogg", "audio/flac", "audio/mp4", "audio/webm",
]);

export interface ChatConversationUI {
  id: string;
  visibilityEpoch?: string | null;
  visibilityGeneration?: number | null;
  visibleAfterSequence?: number | null;
  type: 'individual' | 'group';
  contact?: ChatContactUI;
  group?: ChatGroupUI;
  messages: ChatMessageUI[];
  pinnedMessages?: ChatMessageUI[];
  hasMoreHistory?: boolean;
  hasMoreAfterHistory?: boolean;
  isLoadingHistory?: boolean;
  historyError?: string | null;
  files?: unknown[];
}

export interface ChatTypingUserUI {
  userId: number;
  fullName: string;
}

/**
 * Fila de contacto de Chat derivada de consultas paginadas a People API.
 * Lo usan:
 *
 * - Panel "Contactos" del dashboard chat (Conectados / No conectados).
 * - Popover "Añadir miembros" del grupo.
 * - Browse de contactos antes de iniciar una conversación 1-a-1.
 *
 * <p>Chat actions require a real IAM userId. Employees without that mapping
 * can be displayed but must not be used for direct conversations or presence.
 */
export interface GlobalContactUI {
  /** Stable UI identifier. Uses userId when available, otherwise a non-actionable employee key. */
  id: string;
  userId: number | null;
  employeeId: number;
  fullName: string;
  searchText?: string;
  roleName: string;
  /** Departamento al que pertenece el empleado. Útil para agrupar en futuras vistas. */
  departmentName: string;
  /** Sede del empleado. */
  locationName: string;
  avatarUrl?: string;
  /** Color asignado de forma determinística según userId para los avatares con iniciales. */
  color: string;
  isOnline: boolean;
  /** ISO o relativo formateado ("Hace 5 min") cuando isOnline=false. */
  lastSeen?: string;
}
